import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';

const StarryBackground: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let width = window.innerWidth;
    let height = window.innerHeight;
    canvas.width = width;
    canvas.height = height;

    const resize = () => {
      width = window.innerWidth;
      height = window.innerHeight;
      canvas.width = width;
      canvas.height = height;
    };
    window.addEventListener('resize', resize);

    // Creates static/twinkling stars
    const stars: { x: number; y: number; r: number; opacity: number; dir: number }[] = [];
    for (let i = 0; i < 250; i++) {
      stars.push({
        x: Math.random() * width,
        y: Math.random() * height,
        r: Math.random() * 1.5,
        opacity: Math.random(),
        dir: Math.random() > 0.5 ? 1 : -1,
      });
    }

    // Creates shooting stars
    interface ShootingStar {
      x: number;
      y: number;
      len: number;
      speed: number;
      opacity: number;
      active: boolean;
      wait: number;
    }
    const shootingStars: ShootingStar[] = [];
    for (let i = 0; i < 3; i++) {
      shootingStars.push({
        x: 0,
        y: 0,
        len: 0,
        speed: 0,
        opacity: 0,
        active: false,
        wait: Math.random() * 500,
      });
    }

    const resetShootingStar = (s: ShootingStar) => {
      s.x = Math.random() * width;
      s.y = Math.random() * -100 - 50; // start above screen
      s.len = Math.random() * 80 + 30;
      s.speed = Math.random() * 8 + 6;
      s.opacity = 1;
      s.active = true;
      s.wait = 0;
    };

    let animationFrameId: number;

    const render = () => {
      ctx.clearRect(0, 0, width, height);

      // Draw background stars
      for (const star of stars) {
        star.opacity += 0.01 * star.dir;
        if (star.opacity >= 1) {
          star.opacity = 1;
          star.dir = -1;
        } else if (star.opacity <= 0.1) {
          star.opacity = 0.1;
          star.dir = 1;
        }

        ctx.beginPath();
        ctx.arc(star.x, star.y, star.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${star.opacity * 0.8})`;
        ctx.fill();
      }

      // Draw shooting stars
      for (const s of shootingStars) {
        if (!s.active) {
          s.wait -= 1;
          if (s.wait <= 0) {
            resetShootingStar(s);
          }
          continue;
        }

        s.x -= s.speed * 0.8;
        s.y += s.speed;
        s.opacity -= 0.005;

        if (s.opacity <= 0 || s.x < -100 || s.y > height + 100) {
          s.active = false;
          s.wait = Math.random() * 300 + 100;
          continue;
        }

        ctx.beginPath();
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(s.x + s.len * 0.8, s.y - s.len);
        
        const gradient = ctx.createLinearGradient(s.x, s.y, s.x + s.len * 0.8, s.y - s.len);
        gradient.addColorStop(0, `rgba(255, 255, 255, ${s.opacity})`);
        gradient.addColorStop(1, 'rgba(255, 255, 255, 0)');
        
        ctx.strokeStyle = gradient;
        ctx.lineWidth = 2;
        ctx.stroke();
      }

      animationFrameId = requestAnimationFrame(render);
    };

    render();

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <Canvas ref={canvasRef} />;
};

export default StarryBackground;

const Canvas = styled.canvas`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: -1;
  pointer-events: none;
  background: transparent;
`;
